/**
 * Die Rechnung hinter der Reihenfolge einer Checkliste.
 *
 * Eine Zeile reiht sich mit dem **Mittelwert** ihrer beiden Nachbarn ein. Das
 * schreibt genau eine Zeile, egal wie weit sie wandert — im Gegensatz zum
 * Durchnummerieren, das den ganzen Bereich zwischen altem und neuem Platz
 * anfasst und für jede berührte Zeile einen Sync-Eintrag kostet. Dieselbe
 * Rechnung trägt die Aufgabenliste (`useTaskDragAndDrop`).
 *
 * Reine Funktionen ohne Dexie-Bezug: Die Dexie-Quelle und die Speicher-Quelle
 * rechnen damit dasselbe.
 */

/**
 * Der Wert, mit dem sich eine Zeile zwischen `before` und `after` einreiht.
 *
 * `undefined` heißt „da ist niemand" — vor der ersten Zeile oder hinter der
 * letzten. `null` als Ergebnis heißt: Zwischen den beiden ist kein Platz mehr,
 * die Liste muss einmal neu durchnummeriert werden ({@link renumber}).
 *
 * @example
 * orderBetween(1, 2)          // 1.5
 * orderBetween(undefined, 0)  // -1  — ganz nach vorn
 * orderBetween(4, undefined)  // 5   — ganz nach hinten
 * orderBetween(1, 1)          // null — kein Platz
 */
export function orderBetween(before: number | undefined, after: number | undefined): number | null {
  if (before === undefined && after === undefined) return 0
  if (before === undefined) return after! - 1
  if (after === undefined) return before + 1
  if (before >= after) return null

  const midpoint = (before + after) / 2
  // Zwei Werte können so dicht beieinander liegen, dass ihr Mittelwert auf einen
  // von ihnen fällt — dann gäbe es zwei Zeilen mit derselben Zahl und die
  // Reihenfolge hinge am Zufall. Kommt erst nach ~50 Einschüben an derselben
  // Stelle vor; der Aufrufer nummeriert dann neu.
  if (midpoint <= before || midpoint >= after) return null
  return midpoint
}

/**
 * Ob die Reihenfolge überhaupt eindeutig ist.
 *
 * Zwei Zeilen auf demselben Wert haben keine — welche zuerst kommt, entscheidet
 * dann die Datenbank, und zwar jedes Mal neu. Das passiert, wenn zwei Geräte
 * dieselbe Liste umsortiert haben und der Abgleich beide Werte behält. Wer an
 * einer solchen Liste rechnet, rechnet auf Sand: Erst neu durchnummerieren.
 */
export function hasUniqueOrder(orders: number[]): boolean {
  return new Set(orders).size === orders.length
}

/**
 * Hinter welche Zeile eine gezogene gehört, wenn sie über `overId` losgelassen
 * wird — `null` heißt an den Anfang.
 *
 * Nach unten gezogen rückt sie **hinter** die überfahrene Zeile, nach oben an
 * **deren Platz**, also hinter deren Vorgänger. Ohne diese Fallunterscheidung
 * landete ein Zug nach oben immer eine Zeile zu tief.
 */
export function afterIdForDrop(ids: string[], activeId: string, overId: string): string | null | undefined {
  const from = ids.indexOf(activeId)
  const to = ids.indexOf(overId)
  if (from === -1 || to === -1 || from === to) return undefined
  return to > from ? ids[to]! : (ids[to - 1] ?? null)
}
